// components/sections/contact/StudioLocation.tsx
"use client";

import { useEffect, useState } from "react";
import clsx from "clsx";
import { MapPin, Clock, Navigation } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { HoneycombPattern } from "./HoneycombPattern";
import { STUDIO_HOURS, SOCIAL_LINKS, STUDIO_COORDS } from "@/data/contact-page";

function RealGoogleMap() {
  const { lat, lng } = STUDIO_COORDS;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return (
      <div className="absolute inset-0 bg-[#0e0c09] flex items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-amber-500/20 border-t-amber-500" />
      </div>
    );
  }

  return (
    <div className="relative h-full w-full min-h-[380px] lg:min-h-0 bg-[#0e0c09]">
      <iframe
        title="Queenscraft Studio Location"
        src={`https://maps.google.com/maps?q=${lat},${lng}&t=&z=14&ie=UTF8&iwloc=&output=embed`}
        width="100%"
        height="100%"
        style={{
          border: 0,
          minHeight: "380px",
          filter:
            "grayscale(100%) invert(90%) contrast(115%) sepia(35%) saturate(120%) hue-rotate(15deg)",
        }}
        allowFullScreen
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        className="absolute inset-0 h-full w-full opacity-85 hover:opacity-100 transition-opacity duration-300"
      />

      <div
        className="absolute inset-0 pointer-events-none mix-blend-color-burn"
        style={{
          background:
            "radial-gradient(circle at center, transparent 30%, rgba(11,9,6,0.8) 100%)",
        }}
      />

      <div className="absolute top-5 left-5 pointer-events-none z-10">
        <div className="inline-flex items-center gap-2 rounded-xl bg-[#14110e]/90 px-4 py-2.5 shadow-2xl border border-white/10 backdrop-blur-md">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-500/70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
          </span>
          <span className="text-[11px] font-bold uppercase tracking-[2px] text-stone-200">
            Studio active
          </span>
        </div>
      </div>
    </div>
  );
}

export function StudioLocation() {
  // Same reveal hook as the rest of the page — no more manual
  // ScrollTrigger.refresh()/setTimeout patchwork, which was itself
  // forcing extra layout passes on every scroll.
  const panel = useScrollReveal<HTMLDivElement>();
  const map = useScrollReveal<HTMLDivElement>({ rootMargin: "0px 0px -5% 0px" });

  return (
    <section className="relative w-full bg-[#0b0906] px-5 py-24 sm:px-9 sm:py-32 lg:px-14">
      <HoneycombPattern id="location-dark-hex" opacity={0.03} color="#e3a059" />

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -bottom-48 -right-48 h-[500px] w-[500px] rounded-full bg-amber-950/10 blur-[120px]" />
      </div>

      <div className="mx-auto w-full max-w-[1200px] relative z-[1]">
        <div className="mb-12">
          <p className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[3px] text-amber-500">
            <span className="inline-block h-px w-6 bg-amber-500/50" />
            Visit us
          </p>
          <h2 className="text-[clamp(2rem,3.5vw,2.8rem)] font-extrabold uppercase leading-[0.96] tracking-tight text-[#fcfbf9]">
            The Studio Location
          </h2>
        </div>

        <div className="grid gap-8 lg:grid-cols-[400px_1fr]">
          {/* Info Panel */}
          <div
            ref={panel.ref}
            className={clsx(
              "flex flex-col justify-between rounded-3xl border border-white/5 bg-[#120f0c] p-8 sm:p-10 shadow-2xl relative overflow-hidden",
              "transition-all duration-700 ease-out",
              panel.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            )}
          >
            <div className="absolute top-0 right-0 h-32 w-32 rounded-full bg-amber-500/[0.02] blur-3xl pointer-events-none" />

            <div className="space-y-8 relative z-10">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <MapPin size={16} className="text-amber-500" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[2px] text-stone-500 mb-1">
                    Address
                  </p>
                  <p className="text-[15px] leading-relaxed text-stone-200 font-medium">
                    {STUDIO_COORDS.address}
                    <br />
                    {STUDIO_COORDS.city}
                  </p>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${STUDIO_COORDS.lat},${STUDIO_COORDS.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2.5 inline-flex items-center gap-1.5 text-[12px] font-semibold text-amber-500 hover:text-amber-400 transition-colors"
                  >
                    <Navigation size={12} />
                    Get Directions
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <Clock size={16} className="text-amber-500" />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-[2px] text-stone-500 mb-3">
                    Opening Hours
                  </p>
                  <dl className="divide-y divide-white/[0.04]">
                    {STUDIO_HOURS.map((h) => (
                      <div key={h.day} className="flex items-center justify-between py-2.5 text-[13px]">
                        <dt className="text-stone-400">{h.day}</dt>
                        <dd
                          className={
                            h.time === "Closed"
                              ? "text-stone-600 font-medium"
                              : "font-semibold text-stone-200"
                          }
                        >
                          {h.time}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t border-white/[0.04] relative z-10">
              <p className="text-[10px] font-bold uppercase tracking-[2px] text-stone-500 mb-3">
                Follow our craft
              </p>
              <div className="flex items-center gap-2.5">
                {SOCIAL_LINKS.map(({ label, href, Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/5 bg-[#191511] text-stone-400 transition-all duration-300 hover:border-amber-500/40 hover:bg-amber-500/5 hover:text-amber-500 hover:-translate-y-0.5"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Map Card */}
          <div
            ref={map.ref}
            className={clsx(
              "relative overflow-hidden rounded-3xl border border-white/5 shadow-2xl",
              "transition-all duration-700 ease-out",
              map.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            )}
            style={{ minHeight: "420px" }}
          >
            <RealGoogleMap />
          </div>
        </div>
      </div>
    </section>
  );
}